/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 *
 * The org selector.
 *
 * GET /teams returns EVERY org in one payload, so the whole fleet's teams and
 * members are already in client memory. The tab strip this replaces spent that
 * on a team count and a scope switch, which left three things invisible:
 *
 *   - a person matching the filter in an org you aren't looking at (the tree
 *     said "no match" while the match sat one tab away),
 *   - staged changes parked in another org (only the cart's total hinted at it),
 *   - how many PEOPLE an org has -- the old `(17)` counted teams.
 *
 * The rail answers all three from data already loaded: no extra request.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Typography, makeStyles } from '@material-ui/core';

/** The subset of a team this rail needs; mirrors the page's Team. */
export type OrgTeam = {
  slug: string;
  description: string | null;
  members: string[];
};

export type OrgStat = {
  org: string;
  teams: number;
  /** Distinct logins across the org's teams -- people, not memberships. */
  people: number;
  /** Teams the tree would show as containing a match. */
  teamHits: number;
  /** Distinct logins matching the query, in THIS org. */
  peopleHits: number;
  /**
   * The matching logins, lowercased. Carried so a fleet-wide total can dedupe
   * across orgs: one person in three orgs is one person, and summing peopleHits
   * would report three.
   */
  matchedLogins: string[];
  staged: number;
};

/**
 * Per-org counts for the rail.
 *
 * `teamHits` deliberately uses the SAME predicate as the page's tree matcher
 * (slug, description, or any member contains the query). A rail that counted
 * anything else would be a second source of truth, and the first time it
 * disagreed with the tree the number would be worse than no number.
 */
export function orgStats(args: {
  orgs: Record<string, OrgTeam[]>;
  order: readonly string[];
  query: string;
  stagedByOrg: Record<string, number>;
}): OrgStat[] {
  const { orgs, order, query, stagedByOrg } = args;
  const ql = query.trim().toLowerCase();
  return order
    .filter(org => orgs[org])
    .map(org => {
      const teams = orgs[org];
      const everyone = new Set<string>();
      const matched = new Set<string>();
      let teamHits = 0;
      for (const t of teams) {
        let memberHit = false;
        for (const m of t.members) {
          everyone.add(m.toLowerCase());
          if (ql && m.toLowerCase().includes(ql)) {
            matched.add(m.toLowerCase());
            memberHit = true;
          }
        }
        if (
          ql &&
          (memberHit ||
            t.slug.toLowerCase().includes(ql) ||
            (t.description ?? '').toLowerCase().includes(ql))
        ) {
          teamHits += 1;
        }
      }
      return {
        org,
        teams: teams.length,
        people: everyone.size,
        teamHits,
        peopleHits: matched.size,
        matchedLogins: [...matched],
        staged: stagedByOrg[org] ?? 0,
      };
    });
}

/** `2 people · 3 teams`, pluralised, with no query in play. */
export const describeOrg = (s: OrgStat): string =>
  `${s.people} ${s.people === 1 ? 'person' : 'people'} · ${s.teams} ${
    s.teams === 1 ? 'team' : 'teams'
  }`;

/**
 * What the org contributes to the active query. Returns null when no query is
 * running, so the caller shows the resting description instead.
 */
export const describeHits = (s: OrgStat, query: string): string | null => {
  if (!query.trim()) return null;
  if (s.peopleHits === 0 && s.teamHits === 0) return 'no match';
  const parts: string[] = [];
  if (s.peopleHits > 0) {
    parts.push(
      `${s.peopleHits} ${s.peopleHits === 1 ? 'person' : 'people'}`,
    );
  }
  if (s.teamHits > 0) {
    parts.push(`${s.teamHits} ${s.teamHits === 1 ? 'team' : 'teams'}`);
  }
  return parts.join(' · ');
};

/**
 * Fleet-wide totals for the summary line.
 *
 * `people` is the DISTINCT login count across every org, not the sum of the
 * per-org figures: one person who belongs to three orgs is one person, and
 * summing peopleHits reported them as three. Teams are org-scoped, so those do
 * sum.
 */
export function fleetTotals(stats: OrgStat[]): {
  hitOrgs: OrgStat[];
  people: number;
  teams: number;
} {
  const everyone = new Set<string>();
  let teams = 0;
  for (const s of stats) {
    for (const login of s.matchedLogins) everyone.add(login);
    teams += s.teamHits;
  }
  return {
    hitOrgs: stats.filter(s => s.peopleHits > 0 || s.teamHits > 0),
    people: everyone.size,
    teams,
  };
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SETTLE_MS = 260;

/**
 * Counts up to `target` so a changed query lands as motion rather than a jump.
 * The rail's one authored moment; everything else here is static.
 *
 * Returns `target` immediately under prefers-reduced-motion, and whenever the
 * value shrinks -- counting DOWN reads as loading, not as settling.
 */
function useSettledCount(target: number, delayMs: number): number {
  const [shown, setShown] = useState(target);
  const previous = useRef(target);

  useEffect(() => {
    const from = previous.current;
    previous.current = target;
    if (prefersReducedMotion() || target <= from) {
      setShown(target);
      return undefined;
    }
    let raf = 0;
    let start = 0;
    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start - delayMs) / SETTLE_MS);
      if (t < 0) {
        raf = requestAnimationFrame(step);
        return;
      }
      // Exponential ease-out: fast off the mark, settles rather than stops.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, delayMs]);

  return shown;
}

const useStyles = makeStyles(theme => ({
  rail: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    // The strip this replaces was a scrollable MuiTabs whose internals ran
    // past a 390px viewport. Wrapping keeps every org reachable without a
    // horizontal scroller.
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  org: {
    position: 'relative',
    display: 'block',
    flex: '1 1 auto',
    minWidth: 168,
    textAlign: 'left',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    cursor: 'pointer',
    font: 'inherit',
    color: theme.palette.text.primary,
    transition: 'border-color 160ms ease-out, background-color 160ms ease-out',
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  orgActive: {
    borderColor: theme.palette.primary.main,
    // Selection is carried by the border, the weight of the name AND the rule
    // below -- never by colour alone.
    boxShadow: `inset 0 -3px 0 0 ${theme.palette.primary.main}`,
  },
  // A no-match org recedes, but the words "no match" carry the state for
  // anyone who can't see the dimming.
  orgMuted: {
    opacity: 0.55,
  },
  name: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.4,
  },
  nameActive: {
    fontWeight: 600,
  },
  meta: {
    display: 'block',
    fontSize: '0.75rem',
    lineHeight: 1.5,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.text.secondary,
  },
  metaHit: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  // The authored moment: a matched count arrives slightly soft and settles.
  settling: {
    animation: '$settle 260ms cubic-bezier(0.16, 1, 0.3, 1) both',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes settle': {
    from: { filter: 'blur(3px)', transform: 'translateY(2px)' },
    to: { filter: 'blur(0)', transform: 'none' },
  },
  /**
   * The queued-change count, as a notification badge on the tab's corner.
   *
   * It was an amber bordered pill on a line of its own below the counts, which
   * cost every tab a third line and read as a fourth statistic rather than as
   * "you have work waiting here". A corner badge is the convention for exactly
   * this, and dropping the extra line makes the tabs shorter.
   *
   * primary.main, not error.main: these are queued edits, not errors, and this
   * theme's primary IS the vivid red (#B5371F) while error is a muted maroon
   * (#8c4351). It also matches the cart button, which is the same count.
   */
  stagedBadge: {
    position: 'absolute',
    top: -7,
    right: -7,
    minWidth: 18,
    height: 18,
    boxSizing: 'border-box',
    padding: theme.spacing(0, 0.5),
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6875rem',
    fontWeight: 700,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    // A ring in the page surface, so the badge reads as sitting ON the corner
    // instead of being a shape the tab border cuts through.
    boxShadow: `0 0 0 2px ${theme.palette.background.default}`,
  },
}));

/**
 * The org selector. A real tablist: roving tabindex, arrow/Home/End keys, and
 * aria-controls onto the tree it switches -- the page previously promised tab
 * semantics through MuiTabs, so hand-rolling has to keep that promise rather
 * than leave a div that merely looks like tabs.
 */
export function OrgRail({
  stats,
  value,
  query,
  onChange,
  panelId,
}: {
  stats: OrgStat[];
  value: string;
  query: string;
  onChange: (org: string) => void;
  panelId: string;
}) {
  const classes = useStyles();
  const refs = useRef(new Map<string, HTMLButtonElement>());

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      e.preventDefault();
      const i = stats.findIndex(s => s.org === value);
      if (i === -1) return;
      let next = i;
      if (e.key === 'ArrowLeft') next = (i - 1 + stats.length) % stats.length;
      if (e.key === 'ArrowRight') next = (i + 1) % stats.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = stats.length - 1;
      const target = stats[next];
      onChange(target.org);
      refs.current.get(target.org)?.focus();
    },
    [stats, value, onChange],
  );

  return (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      role="tablist"
      aria-label="Organisation"
      aria-orientation="horizontal"
      className={classes.rail}
      onKeyDown={onKeyDown}
    >
      {stats.map((s, i) => (
        <OrgTab
          key={s.org}
          stat={s}
          query={query}
          selected={s.org === value}
          index={i}
          panelId={panelId}
          onSelect={onChange}
          registerRef={(el: HTMLButtonElement | null) => {
            if (el) refs.current.set(s.org, el);
            else refs.current.delete(s.org);
          }}
        />
      ))}
    </div>
  );
}

function OrgTab({
  stat,
  query,
  selected,
  index,
  panelId,
  onSelect,
  registerRef,
}: {
  stat: OrgStat;
  query: string;
  selected: boolean;
  index: number;
  panelId: string;
  onSelect: (org: string) => void;
  registerRef: (el: HTMLButtonElement | null) => void;
}) {
  const classes = useStyles();
  const hits = describeHits(stat, query);
  const searching = hits !== null;
  const noMatch = searching && stat.peopleHits === 0 && stat.teamHits === 0;
  // Stagger by position so the rail settles left-to-right rather than at once.
  const shownPeople = useSettledCount(stat.peopleHits, index * 40);

  // The accessible name says everything the two visual lines say, in one
  // string, so the rail reads the same to a screen reader as to an eye.
  const label = useMemo(() => {
    let hitPart = '';
    if (searching) {
      hitPart = noMatch
        ? ', no match for the current filter'
        : `, ${hits} matching the current filter`;
    }
    const stagedPart =
      stat.staged > 0 ? `, ${stat.staged} staged changes` : '';
    return `${stat.org}, ${describeOrg(stat)}${hitPart}${stagedPart}`;
  }, [stat, hits, searching, noMatch]);

  return (
    <button
      type="button"
      role="tab"
      ref={registerRef}
      aria-selected={selected}
      aria-controls={panelId}
      aria-label={label}
      // Roving tabindex: one stop for the whole rail, arrows move within it.
      tabIndex={selected ? 0 : -1}
      className={`${classes.org} ${selected ? classes.orgActive : ''} ${
        noMatch ? classes.orgMuted : ''
      }`}
      onClick={() => onSelect(stat.org)}
    >
      <span
        className={`${classes.name} ${selected ? classes.nameActive : ''}`}
        aria-hidden="true"
      >
        {stat.org}
      </span>
      <span
        key={searching ? `hit-${query}` : 'rest'}
        className={`${classes.meta} ${
          searching && !noMatch ? classes.metaHit : ''
        } ${searching ? classes.settling : ''}`}
        aria-hidden="true"
      >
        {searching
          ? renderHits(stat, shownPeople, hits as string)
          : describeOrg(stat)}
      </span>
      {stat.staged > 0 && (
        // aria-hidden: the tab's own accessible name already ends with
        // "N staged changes", so announcing the bare number again would just
        // repeat it.
        <span className={classes.stagedBadge} aria-hidden="true">
          {stat.staged > 99 ? '99+' : stat.staged}
        </span>
      )}
    </button>
  );
}

// While the count-up is mid-flight the people figure is the animated one; the
// rest of the phrase is already final, so it is taken from describeHits.
function renderHits(stat: OrgStat, shownPeople: number, hits: string): string {
  if (stat.peopleHits === 0) return hits;
  const settled = `${shownPeople} ${shownPeople === 1 ? 'person' : 'people'}`;
  const rest =
    stat.teamHits > 0
      ? ` · ${stat.teamHits} ${stat.teamHits === 1 ? 'team' : 'teams'}`
      : '';
  return `${settled}${rest}`;
}

const useSummaryStyles = makeStyles(theme => ({
  summary: {
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
  },
  jump: {
    font: 'inherit',
    color: theme.palette.primary.main,
    background: 'none',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
}));

/**
 * The global answer, directly under the search field and ABOVE the rail.
 *
 * The search is fleet-wide, so it has to report fleet-wide before the rail
 * narrows to one org. The tree can only ever speak for the org on screen --
 * which is how "no match" used to appear while the person sat one tab away.
 * Three cases matter, and only the last is a repeat of anything:
 *
 *   - nothing anywhere: say so once, for the whole fleet.
 *   - matches, but none in the org you're looking at: name where they are and
 *     offer to go, since the tree below is about to look empty.
 *   - matches here and elsewhere: give the totals; the rail carries the split.
 */
export function OrgSearchSummary({
  stats,
  value,
  query,
  onChange,
}: {
  stats: OrgStat[];
  value: string;
  query: string;
  onChange: (org: string) => void;
}) {
  const classes = useSummaryStyles();
  const q = query.trim();
  if (!q) return null;

  const { hitOrgs, people, teams } = fleetTotals(stats);
  const here = stats.find(s => s.org === value);
  const hereHas = Boolean(here && (here.peopleHits > 0 || here.teamHits > 0));

  if (hitOrgs.length === 0) {
    return (
      <Typography className={classes.summary} role="status">
        No team or person matches “{q}” in any org.
      </Typography>
    );
  }

  const scope =
    hitOrgs.length === 1
      ? `in ${hitOrgs[0].org}`
      : `across ${hitOrgs.length} orgs`;
  const parts = [
    people > 0 && `${people} ${people === 1 ? 'person' : 'people'}`,
    teams > 0 && `${teams} ${teams === 1 ? 'team' : 'teams'}`,
  ].filter(Boolean) as string[];

  return (
    <Typography className={classes.summary} role="status">
      “{q}” matches {parts.join(' and ')} {scope}.
      {!hereHas && (
        <>
          {' '}
          None in {value} —{' '}
          <button
            type="button"
            className={classes.jump}
            onClick={() => onChange(hitOrgs[0].org)}
          >
            go to {hitOrgs[0].org}
          </button>
        </>
      )}
    </Typography>
  );
}
